import { defineStore } from 'pinia';
import { ref } from 'vue';
import useUserComposable from '@/composables/UserComposable';
import GlobalRequest from '@/api/global-request';
import { showToast } from '@/helpers/toast-handler';

const LINK_URL = 'v2/template/links';

export const useLinkStore = defineStore('link', () => {
    const { company_id } = useUserComposable();
    const editLinkId = ref(null);
    const link = ref({
        type: 'default', // linkType is only for front purpose
        company_id: null,
        formula_id: null,
        attribute_code: null,
        operator: null,
        value: null,
        step_from: null,
        step_to: null,
        order: null,
    });
    const links = ref([]);
    // types is only for front easibility
    const types = ref([
        { id: 1, name: 'default', label: 'Default' },
        { id: 2, name: 'boolean', label: 'Boolean Formula' },
        { id: 3, name: 'value', label: 'Value Based Formula' },
        { id: 4, name: 'attribute', label: 'Step Attribute Based' },
    ]);
    const operators = ref([
        { label: 'Equals to', value: '==' },
        { label: 'Less than', value: '<' },
        { label: 'Less than or Equals to', value: '<=' },
        { label: 'Greater than', value: '>' },
        { label: 'Greater than or Equals to', value: '>=' },
    ]);

    // helpers
    const initLink = () => {
        return {
            type: 'default', // linkType is only for front purpose
            company_id: company_id.value,
            formula_id: null,
            attribute_code: null,
            operator: null,
            value: null,
            step_from: null,
            step_to: null,
            order: null,
        };
    };

    // this is for initial EditAssessmentStepsAndLinks configuration when assessment details are extracted and loaded into the assessment builder UI
    // this function usually takes the links array from the step store and will populate them into the link states
    const populateStepLinks = (data) => {
        // unlike steps which are independent, links cannot be nullified in the initial step because it can trigger multiple nullification for each step link_to calls
        // so we will nullify links array from step state
        data?.forEach((link) => {
            const linkObj = {
                id: link?.id,
                company_id: company_id.value,
                formula_id: link.formula_id,
                attribute_code: link.attribute_code,
                operator: link.operator,
                value: link.value,
                step_from: link.step_from_id,
                step_to: link.step_to_id,
                order: link.order,
            };
            links.value?.push(linkObj);
        });
    };

    // getters
    const getStepLinks = (stepId) => {
        const filteredLinks = links.value?.filter((link) => link.step_from_id === stepId || link.step_from === stepId);
        return filteredLinks;
    };

    // actions
    const createLink = async (data) => {
        try {
            const response = await GlobalRequest.postRequest(LINK_URL, {
                ...data,
                company_id: company_id.value,
            });
            if (response.statusCode === 201) {
                showToast(response, 'Link created successfully');
                // push the new link created into the links array
                links.value.push(response.data);
                // init link after creation to avoid caches
                link.value = initLink();
            }
            return response.data;
        } catch (error) {
            showToast({}, error?.data.message);
        }
    };

    // setup link details into linkStore link for edit
    const editLink = async (data) => {
        // populate all the values from the data
        editLinkId.value = data.id;
        link.value = {
            company_id: company_id.value,
            formula_id: data.formula_id,
            attribute_code: data.attribute_code,
            operator: data.operator,
            value: data.value ? (typeof parseInt(data.value) === 'number' ? parseInt(data.value) : data.value) : null,
            step_from: data.step_from_id ?? data.step_from,
            step_to: data.step_to_id ?? data.step_to,
            order: data.order,
        };
        // setup the value attribute type for front easibility
        if (data.attribute_code) {
            link.value.type = 'attribute';
        } else if (data.formula_id) {
            if (data.formula?.type == 1) {
                link.value.type = 'boolean';
            } else {
                link.value.type = 'value';
            }
        } else {
            link.value.type = 'default';
        }
    };

    const updateLink = async (data, id = null) => {
        try {
            const editId = id ?? editLinkId.value;
            const response = await GlobalRequest.patchRequest(`${LINK_URL}/${editId}`, {
                ...data,
                company_id: company_id.value,
            });
            if (response.statusCode === 200) {
                showToast(response, 'Link updated successfully');
                // need to update that particular link object in the whole links array here
                // find index first
                const existingLinkIndex = links.value.findIndex((link) => link.id === editLinkId.value);
                // now if that link exists then update that link
                if (existingLinkIndex !== -1) {
                    const updatedLinkObj = {
                        id: response?.data?.id,
                        company_id: company_id.value,
                        formula_id: response?.data?.formula_id,
                        attribute_code: response?.data?.attribute_code,
                        operator: response?.data?.operator,
                        value: response?.data?.value,
                        step_from: response?.data?.step_from_id ?? response?.data?.step_from,
                        step_to: response?.data?.step_to_id ?? response?.data?.step_to,
                        order: response?.data?.order,
                    };
                    // now replace that index with the new one
                    links.value[existingLinkIndex] = updatedLinkObj;
                }
                // debugger;
                // links.value.push(response.data);
                // init link after creation to avoid caches
                link.value = initLink();
            }
            return response.data;
        } catch (error) {
            showToast({}, error?.data.message);
        }
    };

    const deleteLink = async (id) => {
        try {
            const response = await GlobalRequest.deleteRequest(`${LINK_URL}/${id}`, {
                params: { company_id: company_id.value },
            });
            if (response.statusCode === 200) {
                showToast(response, 'Link removed successfully');
                links.value = links.value.filter((link) => link.id !== id);
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete link');
        }
    };

    return {
        // states
        editLinkId,
        link,
        links,
        types,
        operators,
        // // getters
        getStepLinks,
        // formulas,

        // actions
        createLink,
        editLink,
        updateLink,
        deleteLink,
        // helpers
        populateStepLinks,
    };
});
